<template>
  <div class="transform-config-modal">
    <p class="text-muted text-sm mb-3">
      Pas transformaties toe op de configurator-waarde voordat deze naar de leverancier gaat.
      Transformaties worden in volgorde uitgevoerd.
    </p>

    <div
      v-for="(transform, index) in localTransforms"
      :key="index"
      class="transform-row"
    >
      <div class="transform-row__fields">
        <SelectInput
          :id="`tr-op-${index}`"
          :modelValue="transform.operation"
          @update:modelValue="updateTransform(index, 'operation', $event)"
          :options="operationOptions"
          label="Operatie"
          size="sm"
        />

        <TextInput
          v-if="needsOperand(transform.operation)"
          :id="`tr-val-${index}`"
          :modelValue="String(transform.operand || '')"
          @update:modelValue="updateTransform(index, 'operand', Number($event))"
          label="Waarde"
          placeholder="bijv. 10"
          size="sm"
        />

        <SelectInput
          v-if="transform.operation === 'round'"
          :id="`tr-dec-${index}`"
          :modelValue="String(transform.decimals ?? 0)"
          @update:modelValue="updateTransform(index, 'decimals', Number($event))"
          :options="decimalOptions"
          label="Decimalen"
          size="sm"
        />

        <TextInput
          v-if="transform.operation === 'prefix' || transform.operation === 'suffix'"
          :id="`tr-str-${index}`"
          :modelValue="transform.string || ''"
          @update:modelValue="updateTransform(index, 'string', $event)"
          label="Tekst"
          placeholder="bijv. mm"
          size="sm"
        />

        <TextInput
          v-if="transform.operation === 'pad'"
          :id="`tr-len-${index}`"
          :modelValue="String(transform.length || '')"
          @update:modelValue="updateTransform(index, 'length', Number($event))"
          label="Lengte"
          placeholder="bijv. 4"
          size="sm"
        />
      </div>

      <Btn variant="plain" size="xs" icon="trash" @click="removeTransform(index)" />
    </div>

    <Btn variant="dashed" size="sm" class="mt-2" @click="addTransform">
      + Transformatie toevoegen
    </Btn>

    <div class="transform-preview mt-3" v-if="localTransforms.length > 0">
      <Label text="Preview" />
      <div class="transform-preview__example">
        <span class="text-muted">Input: {{ testInput }}</span>
        <span> → </span>
        <strong>Output: {{ previewOutput }}</strong>
      </div>
      <TextInput
        id="tr-test-input"
        v-model="testInput"
        placeholder="Test waarde"
        size="sm"
        class="mt-1"
      />
    </div>

    <div class="modal-actions mt-3">
      <Btn variant="primary" @click="handleSubmit">Opslaan</Btn>
    </div>
  </div>
</template>

<script>
import { defineComponent, ref, computed, onMounted } from 'vue';
import TextInput from '@/components/inputs/TextInput.vue';
import SelectInput from '@/components/inputs/SelectInput.vue';
import Label from '@/components/partials/Label.vue';
import Btn from '@/components/partials/Btn.vue';

export default defineComponent({
  name: 'TransformConfigModal',
  components: { TextInput, SelectInput, Label, Btn },

  props: {
    transforms: { type: Array, default: () => [] },
    onSubmit: { type: Function, default: null },
  },

  setup(props) {
    const localTransforms = ref([]);
    const testInput = ref('200');

    const operationOptions = [
      { value: 'multiply', label: 'Vermenigvuldigen (×)' },
      { value: 'divide', label: 'Delen (÷)' },
      { value: 'round', label: 'Afronden' },
      { value: 'floor', label: 'Naar beneden afronden' },
      { value: 'ceil', label: 'Naar boven afronden' },
      { value: 'prefix', label: 'Prefix toevoegen' },
      { value: 'suffix', label: 'Suffix toevoegen' },
      { value: 'pad', label: 'Nulvulling (pad)' },
      { value: 'to_string', label: 'Naar tekst' },
    ];

    const decimalOptions = [
      { value: '0', label: '0 decimalen' },
      { value: '1', label: '1 decimaal' },
      { value: '2', label: '2 decimalen' },
      { value: '3', label: '3 decimalen' },
    ];

    function needsOperand(operation) {
      return ['multiply', 'divide'].includes(operation);
    }

    function addTransform() {
      localTransforms.value.push({
        operation: 'multiply',
        operand: null,
        decimals: null,
        string: '',
        length: null,
      });
    }

    function updateTransform(index, key, value) {
      localTransforms.value[index] = { ...localTransforms.value[index], [key]: value };
    }

    function removeTransform(index) {
      localTransforms.value.splice(index, 1);
    }

    const previewOutput = computed(() => {
      let val = testInput.value;
      for (const t of localTransforms.value) {
        const num = Number(val);
        switch (t.operation) {
          case 'multiply':
            val = String(num * (t.operand || 1));
            break;
          case 'divide':
            val = t.operand ? String(num / t.operand) : val;
            break;
          case 'round':
            val = String(Number(num.toFixed(t.decimals ?? 0)));
            break;
          case 'floor':
            val = String(Math.floor(num));
            break;
          case 'ceil':
            val = String(Math.ceil(num));
            break;
          case 'prefix':
            val = (t.string || '') + val;
            break;
          case 'suffix':
            val = val + (t.string || '');
            break;
          case 'pad':
            val = val.padStart(t.length || 0, '0');
            break;
          case 'to_string':
            break;
        }
      }
      return val;
    });

    onMounted(() => {
      localTransforms.value = JSON.parse(JSON.stringify(props.transforms));
    });

    function handleSubmit() {
      if (props.onSubmit) {
        props.onSubmit(localTransforms.value);
      }
    }

    return {
      localTransforms,
      testInput,
      operationOptions,
      decimalOptions,
      previewOutput,
      needsOperand,
      addTransform,
      updateTransform,
      removeTransform,
      handleSubmit,
    };
  },
});
</script>

<style scoped lang="scss">
.transform-config-modal {
  .transform-row {
    display: flex;
    align-items: flex-end;
    gap: 0.5rem;
    padding: 0.5rem 0;
    border-bottom: 1px solid var(--border-color-light);

    &__fields {
      display: flex;
      gap: 0.5rem;
      flex: 1;
      flex-wrap: wrap;
    }
  }

  .transform-preview {
    background: var(--bg-secondary);
    padding: 0.75rem;
    border-radius: 0.5rem;

    &__example {
      font-size: 0.875rem;
      margin-bottom: 0.5rem;
    }
  }
}

.modal-actions {
  display: flex;
  justify-content: flex-end;
  gap: 0.5rem;
}

.mt-1 { margin-top: 0.25rem; }
.mt-2 { margin-top: 0.5rem; }
.mt-3 { margin-top: 1rem; }
.mb-3 { margin-bottom: 1rem; }
.text-muted { color: var(--text-secondary); }
.text-sm { font-size: 0.8125rem; }
</style>
